import React from 'react';
import { Box, Container, Typography } from '@mui/material';

// Import local logo files from assets folder
import firebaseLogo from '../../assets/firebase.png';
import gcpLogo from '../../assets/GCP.png';
import azureLogo from '../../assets/Microsoft.png';
import stripeLogo from '../../assets/Stripe.png';

// Updated with richer descriptions for SEO
const TECH_STACK = [
  {
    name: 'Firebase',
    logo: firebaseLogo,
    link: 'https://firebase.google.com',
    description: 'Secure authentication and real-time database for invoicing data',
    relationship: 'Cloud database and authentication provider'
  },
  {
    name: 'Google Cloud Platform',
    logo: gcpLogo,
    link: 'https://cloud.google.com',
    description: 'Scalable cloud infrastructure for reliable performance',
    relationship: 'Cloud infrastructure provider'
  },
  {
    name: 'Microsoft Azure',
    logo: azureLogo,
    link: 'https://azure.microsoft.com',
    description: 'Communication services for secure email notifications',
    relationship: 'Email and communication services provider'
  },
  {
    name: 'Stripe',
    logo: stripeLogo,
    link: 'https://stripe.com',
    description: 'Secure payment processing for subscriptions',
    relationship: 'Payment processor'
  }
];

const TechStackFooter = () => {
  // Add JSON-LD structured data for SEO
  React.useEffect(() => {
    const structuredData = {
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      "name": "Coooly Invoice Management",
      "applicationCategory": "BusinessApplication",
      "operatingSystem": "Web",
      "offers": {
        "@type": "Offer",
        "price": "0",
        "priceCurrency": "GBP"
      },
      "review": {
        "@type": "Review",
        "reviewRating": {
          "@type": "Rating",
          "ratingValue": "5",
          "bestRating": "5"
        },
        "author": {
          "@type": "Person",
          "name": "Dental Professional"
        }
      }
    };

    // Add the structured data to the document head
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(structuredData);
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  return (
    <Box 
      component="section" 
      aria-labelledby="tech-stack-heading"
      sx={{ 
        py: 6,
        borderTop: '1px solid rgba(138, 43, 226, 0.1)',
        bgcolor: '#1E1E2D',
        background: 'linear-gradient(to bottom, #1E1E2D, #18182a)',
      }}
    >
      <Container maxWidth="lg">
        {/* Hidden SEO heading for better accessibility and SEO */}
        <Typography 
          variant="h2" 
          sx={{ 
            position: 'absolute',
            width: '1px',
            height: '1px',
            padding: 0,
            margin: '-1px',
            overflow: 'hidden',
            clip: 'rect(0, 0, 0, 0)',
            whiteSpace: 'nowrap',
            borderWidth: 0
          }}
        >
          Technology Stack Powering Coooly Dental Invoice Software - Firebase, Google Cloud, Azure, and Stripe
        </Typography>
        
        <Typography 
          id="tech-stack-heading"
          variant="subtitle2" 
          component="h2"
          align="center" 
          sx={{ color: 'grey.400', mb: 3, fontSize: '0.9rem' }}
        >
          Built with these powerful technologies:
        </Typography>
        
        <Box 
          sx={{ 
            display: 'flex', 
            justifyContent: 'center', 
            flexWrap: 'wrap',
            gap: { xs: 4, md: 6 },
            mb: 4,
            alignItems: 'center'
          }}
        >
          {TECH_STACK.map((tech) => (
            <Box 
              key={tech.name}
              component="a"
              href={tech.link}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={`${tech.name} - ${tech.description}`}
              title={tech.description}
              sx={{ 
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                textDecoration: 'none',
                transition: 'all 0.3s',
                padding: '12px',
                borderRadius: '8px',
                backgroundColor: 'rgba(255, 255, 255, 0.05)',
                width: '170px',
                height: '110px',
                '&:hover': { 
                  transform: 'scale(1.05)', 
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  '& img': {
                    filter: 'none'
                  }
                }
              }}
            >
              <Box 
                component="img" 
                src={tech.logo} 
                alt={`${tech.name} - ${tech.relationship}`}
                sx={{ 
                  height: { xs: '38px', md: '42px' },
                  width: 'auto',
                  objectFit: 'contain',
                  filter: 'brightness(0) invert(1) opacity(0.85)',
                  transition: 'filter 0.3s',
                  mb: 2,
                  maxWidth: '140px'
                }} 
              />
              <Typography 
                variant="caption" 
                sx={{ color: 'grey.400', fontSize: '0.7rem' }}
              >
                {tech.name}
              </Typography>
            </Box>
          ))}
        </Box>
        
        <Typography 
          variant="caption" 
          align="center" 
          sx={{ 
            color: 'grey.600', 
            display: 'block',
            fontSize: '0.65rem',
            mx: 'auto',
            maxWidth: '800px'
          }}
        >
          All product names, logos, and brands are property of their respective owners. This application is not endorsed by or affiliated with these companies.
        </Typography>
      </Container>
    </Box>
  );
};

export default TechStackFooter;
